/**
 * Synthesise a 4-axis pronunciation panel from any ASR result + the
 * WebAudio prosody score. Output shape mirrors Azure's
 * PronunciationAssessment so the existing PronunciationResultPanel
 * renders either source identically.
 *
 * Inputs by axis:
 *   Accuracy      – diff matches/substitutions, weighted by per-word
 *                   confidence when the ASR provides it (Deepgram).
 *                   Falls back to pure WER-style match rate otherwise.
 *   Fluency       – word timestamps → inter-word gaps + speech rate.
 *   Completeness  – diff deletions / ref words.
 *   Prosody       – analyzeProsody (WebAudio F0 contour).
 *
 * What this CAN'T do that Azure does:
 *   - Phoneme-level scoring (Azure aligns user audio to expected
 *     phonemes; pure ASR doesn't produce phoneme posteriors).
 *   - Per-word "needs improvement" on words that the ASR transcribed
 *     correctly. Here a correct transcription = full credit, modulo
 *     per-word confidence when available.
 * The trade is: no false-pass on mispronounced words. Azure's
 * forced-alignment will agree with the reference even when the user
 * said something else; ASR-grounded synthesis won't.
 */
import { AsrResult, DiffSummary } from '../../../services/asrCompareService';
import {
  PronunciationAssessment,
  WordScore,
} from '../../../components/learning/PronunciationResultPanel';

export interface SynthesizedScore extends PronunciationAssessment {
  debug: {
    wer: number;
    avgConfidence?: number;
    wordsPerSec: number;
    longPauses: number;
    pitchRangeSt: number;
    pitchVarianceSt: number;
    voicedRatio: number;
  };
}

const clamp = (n: number, lo: number, hi: number): number =>
  Math.max(lo, Math.min(hi, n));

interface SynthesisInput {
  diff: DiffSummary;
  asr: AsrResult;
  referenceText: string;
  prosody: {
    score: number;
    pitchRangeSt: number;
    pitchVarianceSt: number;
    voicedRatio: number;
  };
}

export function synthesizeFromAsr(input: SynthesisInput): SynthesizedScore {
  const { diff, asr, referenceText, prosody } = input;
  const refWords = diff.totalRefWords || 1;
  const words = asr.words || [];

  // Per-word confidence aligned to *hypothesis* word order. Only
  // Deepgram returns this today.
  const perWordConfidence = words.map(w => w.confidence).filter((c): c is number => c != null);
  const hasConfidence = perWordConfidence.length > 0
    && perWordConfidence.length >= words.length * 0.8;

  // ── Accuracy ───────────────────────────────────────────────────────
  const matchRate = diff.matches / refWords;
  let avgConfidence: number | undefined;
  let accuracy: number;
  if (hasConfidence) {
    avgConfidence = perWordConfidence.reduce((a, b) => a + b, 0) / perWordConfidence.length;
    // Confidence-weighted: a "match" with low confidence still counts,
    // but partially. Wrong/missing words always score 0.
    accuracy = matchRate * 100 * (0.5 + 0.5 * avgConfidence);
  } else {
    accuracy = matchRate * 100;
  }

  // ── Completeness ───────────────────────────────────────────────────
  const completeness = (1 - diff.deletions / refWords) * 100;

  // ── Fluency ────────────────────────────────────────────────────────
  let longPauses = 0;
  let pausePenalty = 0;
  for (let i = 1; i < words.length; i++) {
    const prevEnd = words[i - 1].end;
    const curStart = words[i].start;
    if (prevEnd != null && curStart != null) {
      const gap = curStart - prevEnd;
      if (gap > 0.5) longPauses++;
      if (gap > 1.0) pausePenalty += 12;
      else if (gap > 0.5) pausePenalty += 6;
    }
  }
  const lastWordEnd = words.length > 0 ? words[words.length - 1].end : undefined;
  const durSec = asr.durationSec || lastWordEnd || 1;
  const wordsPerSec = words.length / Math.max(durSec, 0.1);
  let rateScore = 100;
  if (wordsPerSec < 1.5) rateScore = 100 - (1.5 - wordsPerSec) * 30;
  else if (wordsPerSec > 4.5) rateScore = 100 - (wordsPerSec - 4.5) * 15;
  const fluency = clamp(rateScore - pausePenalty, 0, 100);

  // ── Overall ────────────────────────────────────────────────────────
  // Same default weights Azure publishes (acc 40 / others 20).
  const overall = 0.4 * accuracy + 0.2 * fluency + 0.2 * completeness + 0.2 * prosody.score;

  // ── Per-word breakdown (ref-aligned for the panel) ─────────────────
  const wordScores = buildWordScores(diff, hasConfidence ? perWordConfidence : undefined);

  return {
    overallScore: Math.round(overall),
    accuracyScore: Math.round(accuracy),
    fluencyScore: Math.round(fluency),
    completenessScore: Math.round(completeness),
    prosodyScore: prosody.score,
    words: wordScores,
    transcribedText: asr.transcript,
    expectedText: referenceText,
    debug: {
      wer: diff.wer,
      avgConfidence,
      wordsPerSec,
      longPauses,
      pitchRangeSt: prosody.pitchRangeSt,
      pitchVarianceSt: prosody.pitchVarianceSt,
      voicedRatio: prosody.voicedRatio,
    },
  };
}

function buildWordScores(
  diff: DiffSummary,
  perWordConfidence?: number[],
): WordScore[] {
  // diff.entries are reference-aligned: match / substitute / delete
  // emit one ref-side word; insert advances only the hyp pointer.
  const out: WordScore[] = [];
  let hypIdx = 0;
  for (const entry of diff.entries) {
    if (entry.op === 'match') {
      const conf = perWordConfidence?.[hypIdx] ?? 1;
      out.push({
        word: entry.ref ?? '',
        accuracyScore: Math.round(conf * 100),
        errorType: 'None',
      });
      hypIdx++;
    } else if (entry.op === 'substitute') {
      out.push({
        word: entry.ref ?? '',
        accuracyScore: 0,
        errorType: 'Mispronunciation',
        suggestion: entry.hyp,
      });
      hypIdx++;
    } else if (entry.op === 'delete') {
      out.push({
        word: entry.ref ?? '',
        accuracyScore: 0,
        errorType: 'Omission',
      });
    } else if (entry.op === 'insert') {
      hypIdx++;
    }
  }
  return out;
}
